import React, { useState } from 'react';
import { Trans } from '@lingui/macro';

import {
  DROPDOWN_MAX_HEIGHT,
  DROPDOWN_MAX_HEIGHT_M2,
} from '@rover/constants/src/platform/AutocompleteMultiSelect.constants';
import { ZIndex } from '@rover/constants/src/styles/ZIndex';
import { Box } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import { isMobileBrowser } from '@rover/rsdk/src/modules/Network/userAgent';
import type { AutocompleteSelectOption } from '@rover/types/src/AutocompleteSelectOption';

import AutocompleteDropdownOption from './AutocompleteDropdownOption';

export type Props = {
  options: AutocompleteSelectOption[];
  onOptionClick: (value: string | number) => void;
  noOptionsAvailableFor: string;
  focusedIndex: number;
  onOptionMouseOver: (value: string | number, index: number) => void;
  id?: string;
  ariaActivedescendant?: string;
  ariaOwns?: string;
  maxHeightM2?: boolean;
  selectedValue?: string;
};

const AutocompleteDropdown: React.FC<Props> = (props: Props) => {
  const theme = useTheme();
  const {
    maxHeightM2,
    options,
    onOptionClick,
    noOptionsAvailableFor,
    focusedIndex,
    onOptionMouseOver,
    id,
    ariaActivedescendant,
    ariaOwns,
    selectedValue,
  } = props;
  const [isDragging, setIsDragging] = useState(false);

  if (!options.length && !noOptionsAvailableFor) {
    return null;
  }

  return (
    <Box
      id={id || ''}
      webProps={{ role: 'listbox' }}
      aria-multiselectable
      aria-activedescendant={ariaActivedescendant || ''}
      aria-owns={ariaOwns || ''}
      data-testid={
        noOptionsAvailableFor ? 'autocomplete-dropdown-no-results' : 'autocomplete-dropdown'
      }
      position="absolute"
      background="primary"
      sx={{
        overflowY: 'auto',
        zIndex: ZIndex.DROPDOWN.toString(),
        left: '-1px',
        right: '-1px',
        top: 'calc(100% - 1px)',
        borderRadius: `0 0 ${theme.radii.secondary} ${theme.radii.secondary}`,
        boxShadow: '0px 8px 8px 0px rgba(0, 0, 0, 0.25)',
        margin: '5px 0px 0px 0px',
        maxHeight: maxHeightM2 ? DROPDOWN_MAX_HEIGHT_M2 : DROPDOWN_MAX_HEIGHT,
      }}
    >
      {noOptionsAvailableFor ? (
        <Box
          background="secondary"
          sx={{
            padding: '5px 6px', // Added padding in sx because we don't have this values mapped in SpacingSystemType
          }}
        >
          <Trans>No results match</Trans> {`"${noOptionsAvailableFor}"`}
        </Box>
      ) : (
        <>
          {options.map(({ title, value, id: optionId }, i) => (
            <AutocompleteDropdownOption
              key={optionId}
              index={i}
              id={id ? `${id}-${optionId}` : ''}
              ariaSelected={selectedValue === value}
              onMouseDown={(e: React.SyntheticEvent) => {
                isMobileBrowser() && e.preventDefault();
                onOptionClick(value);
              }}
              onTouchStart={() => {
                setIsDragging(false);
              }}
              onTouchMove={() => {
                setIsDragging(true);
              }}
              onTouchEnd={(e: React.SyntheticEvent) => {
                if (isDragging) return;
                isMobileBrowser() && e.preventDefault();
                onOptionClick(value);
              }}
              onMouseOver={() => onOptionMouseOver(value, i)}
              onFocus={() => onOptionMouseOver(value, i)}
              isFocused={i === focusedIndex}
              maxHeightM2={maxHeightM2}
            >
              {title}
            </AutocompleteDropdownOption>
          ))}
        </>
      )}
    </Box>
  );
};

export default AutocompleteDropdown;
